// ============================================================================
// RENTAL CART SYSTEM - CLEAN & MINIMAL VERSION
// ============================================================================
// CONFIGURATION - Reads from global config set in theme.liquid
const RENTAL_CART_API_BASE_URL = window.__BOOKBUGS_CONFIG?.API_BASE_URL || 'https://order-return-testing-cjlu.onrender.com/api';
// ============================================================================

// Simple Rental Cart Manager
(function() {
  'use strict';

  if (typeof window.RentalCart === 'undefined') {
    window.RentalCart = class RentalCart {
      constructor() {
        this.apiBaseUrl = RENTAL_CART_API_BASE_URL;
        this.cart = [];
        this.customerId = null;
        this.customerPlan = null;
        this.customerPlanName = null;
        this.planLimits = {
          'A': 5, 'C': 5, 'E': 5,    // 5 books max
          'B': 10, 'D': 10, 'F': 10  // 10 books max
        };
        this._currentRentalsCount = 0;
        this._currentRentals = [];       // full rental objects from API
        this._booksMarkedForReturn = []; // variant IDs marked for return
        this._dataLoadFailed = false;    // true if plan/rentals API calls failed
        this.isInitializing = false;
        this.isInitialized = false;
        this.initializationPromise = null;
        this._pendingOperations = [];
        
        // Loading state management for checkout and rentals
        this.isCheckingOut = false;
        this.isRentalsLoading = false;
        this.rentalsLoaded = false;
        this.init();
      }

      async init() {
        // Prevent multiple initializations
        if (this.isInitializing) {
          console.log('🔄 Cart already initializing, waiting for completion...');
          return this.initializationPromise;
        }
        
        if (this.isInitialized) {
          console.log('✅ Cart already initialized');
          return Promise.resolve();
        }
        
        this.isInitializing = true;
        
        this.initializationPromise = this._performInitialization();
        
        try {
          await this.initializationPromise;
          this.isInitialized = true;
          this.isInitializing = false;
          
          // Process any pending operations that occurred during initialization
          await this._processPendingOperations();
          
          console.log('✅ Rental Cart fully initialized and ready');
        } catch (error) {
          console.error('❌ Error initializing rental cart:', error);
          this.isInitializing = false;
          this.isInitialized = false;
        }
        
        return this.initializationPromise;
      }
      
      async _performInitialization() {
        try {
          console.log('🔄 Performing rental cart initialization...');
          
          // Show loading state immediately
          this._setLoadingState(true);
          
          // Get customer ID
          this.customerId = this.getCustomerId();
          console.log('👤 Customer ID found:', this.customerId);
          
          // Load cart from storage FIRST to ensure it's available immediately
          await this.loadCartFromStorage();
          
          // Load return selections from storage
          this._loadReturnSelections();
          
          // Update display with loaded cart (this shows the floating cart if items exist)
          this.updateCartDisplay();
          
          // IMPORTANT: Wait for customer ID to be properly set before proceeding
          if (!this.customerId || this.customerId === 'guest') {
            console.warn('⚠️ No customer ID, using guest mode');
            this.setupEventListeners();
            this._setLoadingState(false);
            return;
          }

          // Load customer plan and rentals count (this can be done in background)
          await this.loadCustomerPlan();
          
          // Update display again with plan information
          this.updateCartDisplay();
          this.setupEventListeners();
          
          this._setLoadingState(false);
          
          console.log('✅ Rental Cart initialization completed for customer:', this.customerId);
        } catch (error) {
          this._setLoadingState(false);
          console.error('❌ Error in cart initialization:', error);
          throw error;
        }
      }
      
      _setLoadingState(isLoading) {
        // Track loading state internally without visual changes
        // This prevents the grey shade effect while maintaining state tracking
        const floatingCart = document.querySelector('.floating-rental-cart');
        if (floatingCart) {
          if (isLoading) {
            floatingCart.classList.add('loading');
          } else {
            floatingCart.classList.remove('loading');
          }
          // Always maintain normal appearance
          floatingCart.style.opacity = '1';
        }
      }
      
      async _processPendingOperations() {
        if (this._pendingOperations.length === 0) return;
        
        console.log(`🔄 Processing ${this._pendingOperations.length} pending operations...`);
        
        for (const operation of this._pendingOperations) {
          try {
            await operation();
          } catch (error) {
            console.error('❌ Error processing pending operation:', error);
          }
        }
        
        this._pendingOperations = [];
        console.log('✅ All pending operations processed');
      }
      
      // Reinitialize cart when customer ID changes
      async reinitializeForNewCustomer() {
        console.log('🔄 Reinitializing cart for new customer...');
        
        const newCustomerId = this.getCustomerId();
        if (newCustomerId !== this.customerId) {
          console.log('👤 Customer ID changed:', {
            old: this.customerId,
            new: newCustomerId
          });
          
          // Clear old cart data
          this.cart = [];
          
          // Update customer ID
          this.customerId = newCustomerId;
          
          // Reinitialize
          await this.init();
        } else {
          console.log('👤 Customer ID unchanged, no reinitialization needed');
        }
      }

      getCustomerId() {
        // Method 1: Try to get customer ID from various sources
        const container = document.querySelector('.book-rental-container, .product-rental-container');
        if (container && container.dataset.customerId) {
          const customerId = container.dataset.customerId;
          console.log('👤 Customer ID found in container dataset:', customerId);
          return customerId;
        }
        
        // Method 2: Check for customer ID in meta tags
        const metaCustomerId = document.querySelector('meta[name="customer-id"]');
        if (metaCustomerId) {
          const customerId = metaCustomerId.content;
          console.log('👤 Customer ID found in meta tag:', customerId);
          return customerId;
        }
        
        // Method 3: Check for customer ID in URL or other page elements
        const urlCustomerId = this.extractCustomerIdFromURL();
        if (urlCustomerId) {
          console.log('👤 Customer ID found in URL:', urlCustomerId);
          return urlCustomerId;
        }
        
        // Method 4: Check for customer ID in page content
        const pageCustomerId = this.extractCustomerIdFromPage();
        if (pageCustomerId) {
          console.log('👤 Customer ID found in page content:', pageCustomerId);
          return pageCustomerId;
        }
        
        console.log('👤 No customer ID found, will use "guest"');
        return 'guest';
      }
      
      extractCustomerIdFromURL() {
        // Check if we're on a cart page with customer ID in URL
        const urlParams = new URLSearchParams(window.location.search);
        const customerId = urlParams.get('customer_id') || urlParams.get('customer');
        if (customerId) return customerId;
        
        // Check if customer ID is in the path
        const pathMatch = window.location.pathname.match(/\/customer\/(\d+)/);
        if (pathMatch) return pathMatch[1];
        
        return null;
      }
      
      extractCustomerIdFromPage() {
        // Look for customer ID in various page elements
        const selectors = [
          '[data-customer-id]',
          '.customer-id',
          '#customer-id',
          '[data-user-id]',
          '.user-id',
          '#user-id'
        ];
        
        for (const selector of selectors) {
          const element = document.querySelector(selector);
          if (element) {
            const customerId = element.dataset.customerId || element.dataset.userId || element.textContent;
            if (customerId && customerId.trim() !== '') {
              return customerId.trim();
            }
          }
        }
        
        return null;
      }

      async loadCustomerPlan() {
        if (!this.customerId || this.customerId === 'guest') {
          this.rentalsLoaded = true;
          return;
        }
        
        try {
          const response = await fetch(`${this.apiBaseUrl}/plan?customer_id=${this.customerId}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          
          const data = await response.json();
          this.customerPlan = data.plan;
          this.customerPlanName = data.planName;
          this._apiPlanLimit = data.limit || null;
          console.log('📋 Customer plan loaded:', this.customerPlan, 'Name:', this.customerPlanName, 'Limit:', this._apiPlanLimit);
          
          // Load rentals count
          await this.getCurrentRentalsCount();
        } catch (error) {
          console.error('❌ Error loading customer plan:', error);
          // Mark loading as done but flag that it failed — checkout will be blocked
          this.isRentalsLoading = false;
          this.rentalsLoaded = true;
          this._dataLoadFailed = true;
        }
      }

      async getCurrentRentalsCount() {
        if (!this.customerId || this.customerId === 'guest') {
          this.rentalsLoaded = true;
          return 0;
        }
        
        this.isRentalsLoading = true;
        this.rentalsLoaded = false;
        
        try {
          console.log('📊 Loading current rentals count...');
          const response = await fetch(`${this.apiBaseUrl}/returns?customer_id=${this.customerId}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          
          const data = await response.json();
          this._currentRentals = data.active || [];
          this._currentRentalsCount = this._currentRentals.length;
          console.log('📊 Current rentals count loaded:', this._currentRentalsCount);
          
          this.rentalsLoaded = true;
          this.isRentalsLoading = false;
          this._dataLoadFailed = false;
          
          // Clean up stale return selections that no longer match any current rental
          this._pruneStaleReturnSelections();
          
          // Auto-clear cart if webhook has confirmed checkout
          this._autoClearIfCheckedOut();
          
          return this._currentRentalsCount;
        } catch (error) {
          console.error('❌ Error getting rentals count:', error);
          this.isRentalsLoading = false;
          this.rentalsLoaded = true;
          this._dataLoadFailed = true;
          return 0;
        }
      }

      getPlanLimit() {
        // Prefer the limit returned by the backend API
        if (this._apiPlanLimit) return this._apiPlanLimit;
        if (!this.customerPlan) return 5;
        const planLetter = this.customerPlan.charAt(0).toUpperCase();
        return this.planLimits[planLetter] || 5;
      }

      // CORE FUNCTIONALITY: Add to cart
      async addToCart(bookData) {
        // If cart is still initializing, queue the operation
        if (!this.isInitialized) {
          console.log('🔄 Cart still initializing, queueing add operation...');
          return new Promise((resolve, reject) => {
            this._pendingOperations.push(async () => {
              try {
                const result = await this._performAddToCart(bookData);
                resolve(result);
              } catch (error) {
                reject(error);
              }
            });
          });
        }
        
        return this._performAddToCart(bookData);
      }
      
      async _performAddToCart(bookData) {
        console.log('📚 Adding book to cart:', bookData);
        if (!bookData.variantId && bookData.productId) {
          console.warn('⚠️ Book data missing variantId, using productId as fallback');
        }
        
        // Previous implementation pre‑validated the variant using
        // /product-status and aborted adds when the flag was false.  that
        // ended up preventing customers from ever getting to checkout when
        // the API returned a transient error.  we now simply log the result
        // (or ignore failures) and allow the item to be added; the order
        // endpoint will drop bad ids if necessary during checkout.
        if (bookData.variantId) {
          try {
            const resp = await fetch(`${this.apiBaseUrl}/product-status/${bookData.variantId}?customer_id=${this.customerId}`);
            if (resp.ok) {
              const info = await resp.json();
              if (info.variantExists === false) {
                console.warn('⚠️ addToCart: variant marked as non‑existent');
                // continue regardless – done for user experience
              }
            }
          } catch (err) {
            console.warn('⚠️ Could not validate variant existence:', err.message);
          }
        }

        // Check cart size limit - cart can hold up to planLimit items
        // (user can mark current rentals for return to make room)
        const planLimit = this.getPlanLimit();
        const cartItems = this.cart.length;
        
        if (cartItems >= planLimit) {
          this.showError(`Your cart is full (${planLimit} books max). Remove a book from cart first.`);
          return false;
        }
        
        // Check if already in cart (compare both variantId and productId if we have them)
        const existingIndex = this.cart.findIndex(item => {
          if (item.variantId && bookData.variantId && item.variantId == bookData.variantId) {
            return true;
          }
          if (item.productId && bookData.productId && item.productId == bookData.productId) {
            return true;
          }
          return false;
        });
        if (existingIndex !== -1) {
          this.showError('This book is already in your rental cart.');
          return false;
        }
        
                 // Add to cart
         const newBook = {
           ...bookData,
           // ensure variantId is always populated for internal logic/UI
           variantId: bookData.variantId || bookData.productId || null,
           productId: bookData.productId || bookData.variantId || null,
           title: this.cleanBookTitle(bookData.title), // Clean the title when adding
           image_url: bookData.image_url || null,
           addedAt: new Date().toISOString()
         };
        
        this.cart.push(newBook);
        console.log('📚 Book added to cart. New length:', this.cart.length);
        
        // Save to storage
        this.saveCartToStorage();
        
        // Update display
        this.updateCartDisplay();
        
                 // Show success message
         const cleanTitle = this.cleanBookTitle(bookData.title);
         this.showSuccess(`"${cleanTitle}" added to rental cart!`);
        
        return true;
      }

      // CORE FUNCTIONALITY: Remove from cart
      removeFromCart(variantId) {
        // If cart is still initializing, queue the operation
        if (!this.isInitialized) {
          console.log('🔄 Cart still initializing, queueing remove operation...');
          this._pendingOperations.push(() => {
            return this._performRemoveFromCart(variantId);
          });
          return false;
        }
        
        return this._performRemoveFromCart(variantId);
      }
      
      _performRemoveFromCart(variantId) {
        console.log('🗑️ Removing variant from cart:', variantId);
        
        const index = this.cart.findIndex(item => item.variantId == variantId);
        if (index === -1) {
          console.warn('⚠️ Book not found in cart for variant:', variantId);
          return false;
        }
        
        const removedBook = this.cart[index];
        this.cart.splice(index, 1);
        console.log('✅ Book removed from cart. New length:', this.cart.length);
        
        // Save to storage
        this.saveCartToStorage();
        
        // Update display
        this.updateCartDisplay();
        
                 // Show success message
         const cleanTitle = this.cleanBookTitle(removedBook.title);
         this.showSuccess(`"${cleanTitle}" removed from cart`);
        
        return true;
      }

      // Storage methods
      saveCartToStorage() {
        try {
          const cartKey = `rental_cart_${this.customerId || 'guest'}`;
          const cartData = JSON.stringify(this.cart);
          
          console.log('💾 === SAVING CART TO STORAGE ===');
          console.log('🔑 Storage key:', cartKey);
          console.log('👤 Customer ID:', this.customerId);
          console.log('📦 Cart data to save:', cartData);
          console.log('📊 Cart length:', this.cart.length);
          
          localStorage.setItem(cartKey, cartData);
          
          // Verify it was saved
          const savedData = localStorage.getItem(cartKey);
          const verification = savedData === cartData;
          console.log('✅ Verification - saved data matches:', verification);
          
          if (!verification) {
            console.error('❌ CRITICAL: Data was not saved correctly!');
            console.log('📦 Expected:', cartData);
            console.log('💾 Actually saved:', savedData);
          }
          
          console.log('💾 === CART SAVE COMPLETED ===');
          
          // Notify other components (e.g. floating-cart-manager)
          document.dispatchEvent(new Event('rentalCartUpdate'));
        } catch (error) {
          console.error('❌ Error saving cart to storage:', error);
        }
      }

      async loadCartFromStorage() {
        try {
          // First, check if we're returning from checkout
          await this.checkCheckoutCompletion();
          
          const cartKey = `rental_cart_${this.customerId || 'guest'}`;
          console.log('📦 === LOADING CART FROM STORAGE ===');
          console.log('🔑 Storage key:', cartKey);
          console.log('👤 Customer ID:', this.customerId);
          
          const savedCart = localStorage.getItem(cartKey);
          console.log('📦 Raw localStorage data:', savedCart);
          
          if (savedCart) {
            const parsedCart = JSON.parse(savedCart);
            this.cart = parsedCart;
            console.log('📦 Cart loaded from storage:', {
              length: this.cart.length,
              items: this.cart.map(item => ({ variantId: item.variantId, title: item.title }))
            });
          } else {
            console.log('📦 No saved cart found');
            this.cart = [];
          }
          
          // Debug: Check all localStorage keys
          const allKeys = Object.keys(localStorage);
          const cartKeys = allKeys.filter(key => key.includes('rental_cart'));
          console.log('🔑 All localStorage keys:', allKeys);
          console.log('🛒 Cart-related keys found:', cartKeys);
          
          cartKeys.forEach(key => {
            try {
              const data = localStorage.getItem(key);
              const parsed = JSON.parse(data);
              console.log(`📦 Key "${key}":`, {
                length: parsed.length,
                items: parsed.map(item => ({ variantId: item.variantId, title: item.title }))
              });
            } catch (error) {
              console.log(`❌ Key "${key}" - Parse error:`, error);
            }
          });
          
          console.log('📦 === CART LOAD COMPLETED ===');
        } catch (error) {
          console.error('❌ Error loading cart from storage:', error);
          this.cart = [];
        }
      }
      
      async checkCheckoutCompletion() {
        try {
          console.log('🔍 Checking if returning from checkout...');
          
          // Get pending checkout cart data
          const pendingCartData = sessionStorage.getItem('pending_checkout_cart');
          if (!pendingCartData) {
            console.log('✅ No pending checkout found');
            return false;
          }
          
          // Parse the pending cart data
          let pendingCart;
          try {
            pendingCart = JSON.parse(pendingCartData);
          } catch (e) {
            console.error('❌ Error parsing pending checkout cart:', e);
            sessionStorage.removeItem('pending_checkout_cart');
            return false;
          }
          
          console.log('🔍 Pending checkout cart found from:', new Date(pendingCart.timestamp).toLocaleString());
          
          // Check URL parameters for checkout completion
          const urlParams = new URLSearchParams(window.location.search);
          const checkoutCompleted = urlParams.get('checkout_completed') === 'true';
          const checkoutSuccess = urlParams.get('checkout_success') === 'true';
          
          if (checkoutCompleted || checkoutSuccess) {
            console.log('🎉 Checkout completed successfully! Clearing cart...');
            // Clear the cart in localStorage
            this.clearCart();
            // Clear the pending checkout data
            sessionStorage.removeItem('pending_checkout_cart');
            // Hide the floating cart if it exists
            const floatingCart = document.querySelector('.floating-rental-cart');
            if (floatingCart) {
              floatingCart.style.display = 'none';
            }
            return true;
          } else {
            // Check if we're back on the site but checkout wasn't completed
            console.log('🔄 Back without checkout completion, restoring cart...');
            this.cart = pendingCart.items || [];
            this.saveCartToStorage();
            sessionStorage.removeItem('pending_checkout_cart');
            return true;
          }
        } catch (error) {
          console.error('❌ Error checking checkout completion:', error);
          return false;
        }
      }

      clearCart() {
        this.cart = [];
        this._booksMarkedForReturn = [];
        this.saveCartToStorage();
        this.updateCartDisplay();
        console.log('🧹 Cart cleared');
      }

      // ==========================================
      // RETURN MANAGEMENT (mark books for return)
      // ==========================================

      toggleReturnBook(variantId) {
        const idx = this._booksMarkedForReturn.indexOf(variantId);
        if (idx === -1) {
          this._booksMarkedForReturn.push(variantId);
          console.log('📌 Marked for return:', variantId);
        } else {
          this._booksMarkedForReturn.splice(idx, 1);
          console.log('📌 Unmarked for return:', variantId);
        }
        // Persist to storage so it survives page nav
        this._saveReturnSelections();
        this.updateCartDisplay();
      }

      _saveReturnSelections() {
        try {
          const key = `rental_returns_${this.customerId || 'guest'}`;
          sessionStorage.setItem(key, JSON.stringify(this._booksMarkedForReturn));
        } catch (e) { console.error('❌ Error saving return selections:', e); }
      }

      _loadReturnSelections() {
        try {
          const key = `rental_returns_${this.customerId || 'guest'}`;
          const saved = sessionStorage.getItem(key);
          if (saved) {
            this._booksMarkedForReturn = JSON.parse(saved);
            console.log('📌 Loaded return selections:', this._booksMarkedForReturn.length);
          }
        } catch (e) { console.error('❌ Error loading return selections:', e); }
      }

      _clearReturnSelections() {
        this._booksMarkedForReturn = [];
        try {
          const key = `rental_returns_${this.customerId || 'guest'}`;
          sessionStorage.removeItem(key);
        } catch (e) { /* ignore */ }
      }

      // Remove return selections for books no longer in active rentals
      _pruneStaleReturnSelections() {
        if (this._booksMarkedForReturn.length === 0) return;
        const activeVariantIds = new Set(this._currentRentals.map(r => String(r.variant_id)));
        const before = this._booksMarkedForReturn.length;
        this._booksMarkedForReturn = this._booksMarkedForReturn.filter(id => activeVariantIds.has(String(id)));
        if (this._booksMarkedForReturn.length !== before) {
          console.log(`🧹 Pruned ${before - this._booksMarkedForReturn.length} stale return selections`);
          this._saveReturnSelections();
        }
      }

      // If every item in the localStorage cart is now an active rental,
      // the Shopify order webhook has confirmed the checkout — clear the cart.
      _autoClearIfCheckedOut() {
        if (this.cart.length === 0 || this._currentRentals.length === 0) return;

        const activeVariantIds = new Set(
          this._currentRentals.map(r => String(r.variant_id))
        );

        const allInRentals = this.cart.every(item =>
          activeVariantIds.has(String(item.variantId))
        );

        if (allInRentals) {
          console.log('🎉 All cart items found in active rentals — checkout confirmed by webhook. Clearing cart.');
          this.cart = [];
          this._booksMarkedForReturn = [];
          this.saveCartToStorage();
          this._clearReturnSelections();
          this.updateCartDisplay();
          sessionStorage.removeItem('pending_checkout_cart');
        }
      }

      // Calculate effective available slots accounting for marked returns
      _getEffectiveSlots() {
        const planLimit = this.getPlanLimit();
        const currentRentals = this._currentRentalsCount || 0;
        const cartItems = this.cart.length;
        const returnsCount = this._booksMarkedForReturn.length;
        const effectiveRentals = Math.max(0, currentRentals - returnsCount);
        const totalAfter = effectiveRentals + cartItems;
        return {
          planLimit,
          currentRentals,
          cartItems,
          returnsCount,
          effectiveRentals,
          totalAfter,
          needsReturns: (currentRentals + cartItems) > planLimit,
          returnsNeeded: Math.max(0, (currentRentals + cartItems) - planLimit),
          canCheckout: totalAfter === planLimit
        };
      }

      // Display methods
      updateCartDisplay() {
        console.log('🔄 Updating cart display, cart length:', this.cart.length);
        
        this.updateCartCount();
        this.updateCartPage();
        this.updateDebugInfo();
      }

      updateCartCount() {
        // Update cart count elements
        const cartCountElements = document.querySelectorAll('.rental-cart-count, .cart-count');
        cartCountElements.forEach(element => {
          element.textContent = this.cart.length;
          element.style.display = this.cart.length > 0 ? 'inline' : 'none';
        });
        
        // Check if we've just completed a checkout
        const urlParams = new URLSearchParams(window.location.search);
        const checkoutCompleted = urlParams.get('checkout_completed') === 'true' || 
                                urlParams.get('checkout_success') === 'true';
        
        // Update floating cart visibility with persistence logic
        const floatingCart = document.querySelector('.floating-rental-cart');
        if (floatingCart) {
          // Hide the cart if checkout was just completed or cart is empty
          if (checkoutCompleted || this.cart.length === 0) {
            floatingCart.style.display = 'none';
            console.log('🛒 Floating cart hidden (checkout completed or cart empty)');
          } else if (this.cart.length > 0) {
            // Always show floating cart when items exist
            floatingCart.style.display = 'block';
            // Don't add 'show' class to avoid animation that causes grey shade
            // floatingCart.classList.add('show');
            
            // Ensure it stays visible across page navigation
            floatingCart.style.position = 'fixed';
            floatingCart.style.zIndex = '9999';
            // Ensure full opacity
            floatingCart.style.opacity = '1';
            
            console.log('🛒 Floating cart shown and made persistent with', this.cart.length, 'items');
          } else {
            // Only hide if cart is actually empty and initialization is complete
            if (this.isInitialized) {
              floatingCart.style.display = 'none';
              // floatingCart.classList.remove('show');
              console.log('🛒 Floating cart hidden (cart empty)');
            } else {
              // Keep visible during initialization to prevent flicker
              console.log('🛒 Keeping floating cart visible during initialization');
            }
          }
        }
      }

      updateCartPage() {
        const cartPage = document.querySelector('.rental-cart-page');
        if (cartPage) {
          this.renderCartPage();
        }
      }

      async renderCartPage() {
        console.log('🔍 === RENDERING CART PAGE ===');
        console.log('📊 Current cart state:', {
          length: this.cart.length,
          items: this.cart.map(item => ({ variantId: item.variantId, title: item.title }))
        });
        
        const cartContainer = document.querySelector('.cart-items-container');
        const cartSummary = document.querySelector('.cart-summary');
        const checkoutBtn = document.querySelector('.checkout-btn');
        
        console.log('🔍 Elements found:', {
          cartContainer: !!cartContainer,
          cartSummary: !!cartSummary,
          checkoutBtn: !!checkoutBtn
        });
        
        if (!cartContainer) {
          console.error('❌ Cart container not found!');
          // Try to find alternative containers
          const alternatives = document.querySelectorAll('[class*="cart"], [id*="cart"]');
          console.log('🔍 Alternative cart containers found:', alternatives.length);
          alternatives.forEach((alt, index) => {
            console.log(`🔍 Alternative ${index + 1}:`, alt.className, alt.id);
          });
          return;
        }

        // Hide loading state
        const loadingCart = cartContainer.querySelector('.loading-cart');
        if (loadingCart) {
          console.log('📦 Hiding loading state');
          loadingCart.style.display = 'none';
        }

        if (this.cart.length === 0) {
          console.log('📦 Cart is empty, showing empty state');
          
          // Add class to cart content for proper centering
          const cartContent = document.querySelector('.cart-content');
          if (cartContent) {
            cartContent.classList.add('cart-empty');
          }
          
          cartContainer.innerHTML = `
            <div class="empty-cart">
              <div class="empty-cart-icon">📖</div>
              <h3>Your Rental Cart is Empty</h3>
              <p>Discover amazing books and start building your reading collection!</p>
              <a href="/collections/all-books" class="browse-books-btn">Browse Books</a>
            </div>
          `;
          if (cartSummary) cartSummary.style.display = 'none';
          if (checkoutBtn) checkoutBtn.style.display = 'none';
          return;
        }

        console.log('📦 Rendering cart with', this.cart.length, 'items');
        
        // Remove empty cart class since we have items
        const cartContent = document.querySelector('.cart-content');
        if (cartContent) {
          cartContent.classList.remove('cart-empty');
        }

                 // Render cart items
         const cartItemsHTML = this.cart.map((item, index) => {
           console.log(`🔍 Rendering cart item ${index + 1}:`, item);
           
           // Clean the title for display
           const cleanTitle = this.cleanBookTitle(item.title);
           
           let imageHTML = '';
           if (item.image_url && item.image_url.trim() !== '') {
             imageHTML = `<img src="${item.image_url}" alt="${cleanTitle}" onerror="this.parentNode.innerHTML='<div class=\\'placeholder-image-with-letter\\'>${cleanTitle.charAt(0).toUpperCase()}</div>'">`;
           } else {
             const firstLetter = cleanTitle.charAt(0).toUpperCase();
             imageHTML = `<div class="placeholder-image-with-letter">${firstLetter}</div>`;
           }
           
           return `
             <div class="cart-item" data-variant-id="${item.variantId}">
               <div class="item-image">
                 ${imageHTML}
               </div>
               <div class="item-details">
                 <h4 class="item-title">${cleanTitle}</h4>
                 <p class="item-variant">Variant ID: ${item.variantId}</p>
                 <p class="item-added">Added: ${new Date(item.addedAt).toLocaleDateString()}</p>
               </div>
               <div class="item-actions">
                 <button class="remove-btn" data-variant-id="${item.variantId}" title="Remove from cart">×</button>
               </div>
             </div>
           `;
         }).join('');

        console.log('📦 Generated HTML length:', cartItemsHTML.length);
        cartContainer.innerHTML = cartItemsHTML;
        console.log('📦 HTML inserted into container');
        
        // Set up remove button event listeners using event delegation
        this.setupRemoveButtonListeners();

        // Render current-rentals return section if cart + rentals exceed plan limit
        const slots = this._getEffectiveSlots();
        let returnsHTML = '';
        if (slots.needsReturns && this._currentRentals.length > 0) {
          returnsHTML = this._renderReturnSection(slots);
        }
        // Insert or update return section inside cart-items-container (below cart items)
        let returnSection = document.querySelector('.return-section');
        if (returnsHTML) {
          if (returnSection) {
            returnSection.outerHTML = returnsHTML;
          } else {
            cartContainer.insertAdjacentHTML('beforeend', returnsHTML);
          }
          this._setupReturnToggleListeners();
        } else if (returnSection) {
          returnSection.remove();
        }

                 // Update summary with proper async handling
         if (cartSummary) {
           try {
             console.log('📊 Updating cart summary...');
             const planLimit = slots.planLimit;
             const currentRentals = slots.currentRentals;
             const cartItems = slots.cartItems;
             const returnsCount = slots.returnsCount;
             const effectiveRentals = slots.effectiveRentals;
             const totalAfter = slots.totalAfter;
             
             console.log('📊 Summary data:', { planLimit, currentRentals, cartItems, returnsCount, totalAfter });
             
             // Status messaging
             let statusClass = 'summary-item';
             let statusMessage = '';
             
             if (slots.needsReturns && returnsCount < slots.returnsNeeded) {
               statusClass = 'summary-item warning';
               statusMessage = `⚠️ Mark ${slots.returnsNeeded - returnsCount} more book${(slots.returnsNeeded - returnsCount) > 1 ? 's' : ''} for return to checkout`;
             } else if (slots.canCheckout && totalAfter === planLimit) {
               statusMessage = '✅ Ready to checkout';
             } else if (totalAfter < planLimit && cartItems > 0) {
               statusMessage = `ℹ️ ${planLimit - totalAfter} slot${(planLimit - totalAfter) > 1 ? 's' : ''} remaining`;
             }
             
             // IMPORTANT: Only update the summary-content div, not the entire cartSummary
             const summaryContent = cartSummary.querySelector('.summary-content');
             if (summaryContent) {
               summaryContent.innerHTML = `
                 <div class="summary-item">
                   <span>Plan Limit:</span>
                   <span>${planLimit} books</span>
                 </div>
                 <div class="summary-item">
                   <span>Current Rentals:</span>
                   <span>${currentRentals} books</span>
                 </div>
                 <div class="summary-item">
                   <span>In Cart:</span>
                   <span>${cartItems} books</span>
                 </div>
                 ${returnsCount > 0 ? `<div class="summary-item">
                   <span>Marked for Return:</span>
                   <span>${returnsCount} books</span>
                 </div>` : ''}
                 <div class="summary-item">
                   <span>Total After Checkout:</span>
                   <span>${totalAfter} / ${planLimit} books</span>
                 </div>
                 ${statusMessage ? `<div class="${statusClass}">
                   <span>Status:</span>
                   <span>${statusMessage}</span>
                 </div>` : ''}
               `;
             }
             
             // Always show the cart summary when there are items
             cartSummary.style.display = 'block';
             console.log('✅ Cart summary updated and shown');
           } catch (error) {
             console.error('❌ Error updating cart summary:', error);
             const summaryContent = cartSummary.querySelector('.summary-content');
             if (summaryContent) {
               summaryContent.innerHTML = `
                 <div class="summary-item">
                   <span>Error loading plan info</span>
                   <span>Please refresh the page</span>
                 </div>
               `;
             }
             cartSummary.style.display = 'block';
           }
         } else {
           console.warn('⚠️ Cart summary element not found');
         }
        
                 // Show/hide checkout button and message
         if (checkoutBtn) {
           try {
             // Check if rentals are still loading or checkout is in progress
             const isLoadingState = this.isRentalsLoading || this.isCheckingOut || !this.rentalsLoaded;
             
             if (this._dataLoadFailed && slots.cartItems > 0) {
               // Data failed to load — show retry, never allow checkout
               checkoutBtn.style.display = 'block';
               checkoutBtn.disabled = false;
               checkoutBtn.textContent = '⚠️ Could not load rentals — Tap to Retry';
               checkoutBtn.style.opacity = '1';
               // Temporarily swap handler to retry loading
               checkoutBtn.onclick = async (e) => {
                 e.preventDefault();
                 checkoutBtn.disabled = true;
                 checkoutBtn.textContent = 'Retrying...';
                 this._dataLoadFailed = false;
                 await this.loadCustomerPlan();
                 this.updateCartDisplay();
               };
             } else if (slots.canCheckout && !isLoadingState) {
               checkoutBtn.style.display = 'block';
               checkoutBtn.disabled = false;
               checkoutBtn.onclick = null; // clear any retry handler
               const parts = [];
               parts.push(`Rent ${slots.cartItems}`);
               if (slots.returnsCount > 0) parts.push(`Return ${slots.returnsCount}`);
               checkoutBtn.textContent = `Place Order (${parts.join(' · ')})`;
               checkoutBtn.style.opacity = '1';
             } else if (slots.cartItems > 0 && slots.needsReturns && slots.returnsCount < slots.returnsNeeded && !isLoadingState) {
               checkoutBtn.style.display = 'block';
               checkoutBtn.disabled = true;
               checkoutBtn.onclick = null;
               checkoutBtn.textContent = `Mark ${slots.returnsNeeded - slots.returnsCount} more for return`;
               checkoutBtn.style.opacity = '0.7';
             } else if (slots.cartItems > 0 && slots.totalAfter < slots.planLimit && !isLoadingState) {
               checkoutBtn.style.display = 'block';
               checkoutBtn.disabled = true;
               checkoutBtn.onclick = null;
               checkoutBtn.textContent = `Fill all slots to place order`;
               checkoutBtn.style.opacity = '0.7';
             } else if (slots.cartItems > 0 && isLoadingState) {
               checkoutBtn.style.display = 'block';
               checkoutBtn.disabled = true;
               checkoutBtn.onclick = null;
               checkoutBtn.style.opacity = '0.7';
               if (this.isCheckingOut) {
                 checkoutBtn.textContent = 'Processing Order...';
               } else {
                 checkoutBtn.textContent = 'Loading...';
               }
             } else {
               checkoutBtn.style.display = 'none';
               checkoutBtn.disabled = true;
               checkoutBtn.onclick = null;
             }
             
             const disabledMessage = document.querySelector('.checkout-disabled-message');
             if (disabledMessage) disabledMessage.style.display = 'none';
           } catch (error) {
             console.error('❌ Error updating checkout button:', error);
             checkoutBtn.style.display = 'none';
           }
         } else {
           console.warn('⚠️ Checkout button element not found');
         }
        
        console.log('✅ === CART PAGE RENDERING COMPLETED ===');
      }

      setupRemoveButtonListeners() {
        const removeButtons = document.querySelectorAll('.remove-btn');
        console.log(`🔘 Setting up ${removeButtons.length} remove buttons`);
        
        removeButtons.forEach(button => {
          const variantId = button.dataset.variantId;
          
          // Remove existing listeners
          button.replaceWith(button.cloneNode(true));
          
          // Get new button reference
          const newButton = document.querySelector(`[data-variant-id="${variantId}"]`);
          if (newButton) {
            newButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🖱️ Remove button clicked for variant:', variantId);
              this.removeFromCart(variantId);
            });
          }
        });
      }

      // ==========================================
      // RETURN SECTION RENDERING
      // ==========================================

      _renderReturnSection(slots) {
        const returnsNeeded = slots.returnsNeeded;
        const returnsCount = slots.returnsCount;
        const remaining = Math.max(0, returnsNeeded - returnsCount);

        const rentalsHTML = this._currentRentals.map(rental => {
          const isMarked = this._booksMarkedForReturn.includes(String(rental.variant_id));
          const cleanTitle = this.cleanBookTitle(rental.title || 'Unknown Book');
          
          let imageHTML = '';
          if (rental.image_url && rental.image_url.trim() !== '') {
            imageHTML = `<img src="${rental.image_url}" alt="${cleanTitle}" class="return-item__image" onerror="this.parentNode.innerHTML='<div class=\\'placeholder-image-with-letter\\'>${cleanTitle.charAt(0).toUpperCase()}</div>'">`;
          } else {
            const firstLetter = cleanTitle.charAt(0).toUpperCase();
            imageHTML = `<div class="placeholder-image-with-letter">${firstLetter}</div>`;
          }

          return `
            <div class="return-item ${isMarked ? 'return-item--marked' : ''}" data-return-variant="${rental.variant_id}">
              <div class="return-item__image-wrap">
                ${imageHTML}
              </div>
              <div class="return-item__info">
                <h4 class="return-item__title">${cleanTitle}</h4>
                <p class="return-item__author">${rental.author || ''}</p>
              </div>
              <div class="return-item__status">
                <div class="return-check">${isMarked ? '✓' : ''}</div>
              </div>
            </div>
          `;
        }).join('');

        return `
          <div class="return-section">
            <div class="return-section__header">
              <h3>📦 Select Books to Return</h3>
              <p class="return-section__info">
                Your plan allows <strong>${slots.planLimit}</strong> books. You have <strong>${slots.currentRentals}</strong> rented 
                and <strong>${slots.cartItems}</strong> in cart.
                ${remaining > 0 
                  ? `Mark <strong>${remaining}</strong> more for return to checkout.`
                  : `<span class="return-section__ready">✅ Ready to checkout!</span>`
                }
              </p>
            </div>
            <div class="return-section__items">
              ${rentalsHTML}
            </div>
          </div>
        `;
      }

      _setupReturnToggleListeners() {
        const items = document.querySelectorAll('.return-item');
        items.forEach(item => {
          item.addEventListener('click', () => {
            const variantId = item.dataset.returnVariant;
            console.log('📌 Return toggle for variant:', variantId);
            this.toggleReturnBook(String(variantId));
          });
        });
      }

      setupEventListeners() {
        // Listen for cart updates
        document.addEventListener('rentalCartUpdate', () => {
          this.updateCartDisplay();
        });

        // Setup checkout button
        const checkoutBtn = document.querySelector('.checkout-btn');
        if (checkoutBtn) {
          checkoutBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.proceedToCheckout();
          });
        }
        
        // Check URL parameters on page load for checkout status
        this.checkURLForCheckoutStatus();
        
        // Also check URL parameters when the page history changes
        window.addEventListener('popstate', () => {
          this.checkURLForCheckoutStatus();
        });
      }
      
      checkURLForCheckoutStatus() {
        const urlParams = new URLSearchParams(window.location.search);
        const checkoutCompleted = urlParams.get('checkout_completed') === 'true';
        const checkoutSuccess = urlParams.get('checkout_success') === 'true';
        
        if (checkoutCompleted || checkoutSuccess) {
          console.log('🎉 Detected checkout completion in URL parameters');
          // Clear cart and hide floating cart
          this.clearCart();
          const floatingCart = document.querySelector('.floating-rental-cart');
          if (floatingCart) {
            floatingCart.style.display = 'none';
          }
          // Clear the pending checkout data
          sessionStorage.removeItem('pending_checkout_cart');
        }
      }

      updateDebugInfo() {
        const debugPlan = document.getElementById('debug-plan');
        if (debugPlan) {
          if (this.customerPlanName) {
            debugPlan.textContent = this.customerPlanName;
          } else if (this.customerPlan) {
            debugPlan.textContent = this.customerPlan;
          } else if (this.customerId && this.customerId !== 'guest') {
            debugPlan.textContent = 'Loading...';
          } else {
            debugPlan.textContent = 'Guest user';
          }
        }
      }

      async proceedToCheckout() {
        // Prevent multiple checkout attempts
        if (this.isCheckingOut) {
          console.log('🔒 Checkout already in progress, ignoring duplicate request');
          this.showError('Checkout is already in progress. Please wait...');
          return;
        }
        
        if (this.cart.length === 0) {
          this.showError('Your rental cart is empty.');
          return;
        }
        
        // Wait for rentals to load before proceeding
        if (this.isRentalsLoading || !this.rentalsLoaded) {
          this.showError('Loading your current rentals. Please wait a moment and try again.');
          return;
        }
        
        // Block checkout if rental data failed to load
        if (this._dataLoadFailed) {
          this.showError('Could not verify your current rentals. Please refresh the page and try again.');
          return;
        }
        
        this.isCheckingOut = true;
        
        // Update button state to show loading
        const checkoutBtn = document.querySelector('.checkout-btn');
        if (checkoutBtn) {
          checkoutBtn.disabled = true;
          checkoutBtn.textContent = 'Processing Order...';
          checkoutBtn.style.opacity = '0.7';
        }
        
        try {
          // Fresh validation with most recent data
          await this.getCurrentRentalsCount(); // Refresh rentals count
          
          const slots = this._getEffectiveSlots();
          
          console.log('🔍 Final checkout validation:', {
            planLimit: slots.planLimit,
            currentRentals: slots.currentRentals,
            cartItems: slots.cartItems,
            returnsCount: slots.returnsCount,
            totalAfter: slots.totalAfter,
            canCheckout: slots.canCheckout
          });
          
          if (!slots.canCheckout) {
            if (slots.needsReturns && slots.returnsCount < slots.returnsNeeded) {
              this.showError(`Please mark ${slots.returnsNeeded - slots.returnsCount} more book${(slots.returnsNeeded - slots.returnsCount) > 1 ? 's' : ''} for return before checking out.`);
            } else {
              this.showError(`Cannot checkout: You would have ${slots.totalAfter} books but your plan allows ${slots.planLimit}.`);
            }
            return;
          }
          
          console.log('🚀 Proceeding to checkout with final validation passed');

          // Build payload: send full item objects so backend can resolve
          // either variantId or productId as needed.
          const payloadItems = this.cart.map(item => ({
            variantId: item.variantId,
            productId: item.productId
          }));
          console.log('📤 Checkout payload items:', payloadItems);

          // Include return variant IDs if any books are marked for return
          const returnVariantIds = this._booksMarkedForReturn.length > 0 
            ? [...this._booksMarkedForReturn] 
            : undefined;
          if (returnVariantIds) {
            console.log('📤 Return variant IDs:', returnVariantIds);
          }

          const response = await fetch(`${this.apiBaseUrl}/order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customer_id: this.customerId,
              items: payloadItems,
              returnVariantIds: returnVariantIds
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Server error:', errorText);
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }
          
          const result = await response.json();
          console.log('✅ Checkout response:', result);
          
          if (result.success && result.checkoutUrl) {
            // Warn if the server dropped any invalid items during cart creation
            if (result.removedIds && result.removedIds.length) {
              console.warn('⚠️ Backend removed invalid item ids before checkout:', result.removedIds);
              // Remove those items from our local cart so they don't get sent again
              this.cart = this.cart.filter(item => !result.removedIds.includes(item.variantId) && !result.removedIds.includes(item.productId));
              this.saveCartToStorage();
              this.updateCartDisplay();
              this.showWarning(`Some books could not be added and were removed: ${result.removedIds.join(', ')}`);
            }

            // Check if using fallback checkout URL
            if (result.usedFallback) {
              console.log('ℹ️ Redirecting to storefront checkout URL (fallback):', result.checkoutUrl);
              this.showWarning('Redirecting to checkout; please complete your order.');
            } else {
              console.log('🎉 Redirecting to normal checkout:', result.checkoutUrl);
            }
            
            // Store cart data in sessionStorage before redirecting
            // This will be used to restore the cart if checkout is abandoned
            const cartData = {
              items: this.cart,
              timestamp: new Date().getTime()
            };
            sessionStorage.setItem('pending_checkout_cart', JSON.stringify(cartData));
            
            // Clear return selections since order is being placed
            this._clearReturnSelections();
            
            // Redirect to checkout without clearing the cart
            // Cart will be cleared only after confirmed successful checkout
            window.location.href = result.checkoutUrl;
          } else {
            console.error('❌ Checkout failed:', result);
            // do not purge the cart when checkout failed – keep the items so
            // the customer can try again or remove them manually. only trim
            // removedIds if the operation actually succeeded earlier.
            if (result.success && result.removedIds && result.removedIds.length) {
              this.cart = this.cart.filter(item => !result.removedIds.includes(item.variantId) && !result.removedIds.includes(item.productId));
              this.saveCartToStorage();
              this.updateCartDisplay();
            }
            this.showError(result.message || 'Error creating rental order. Please try again.');
          }
        } catch (error) {
          console.error('❌ Error proceeding to checkout:', error);
          this.showError(`Error creating rental order: ${error.message}`);
        } finally {
          // Reset checkout state
          this.isCheckingOut = false;
          
          // Reset button state
          if (checkoutBtn) {
            checkoutBtn.disabled = false;
            checkoutBtn.style.opacity = '1';
            checkoutBtn.textContent = `Place Rental Order (${this.cart.length} books)`;
          }
        }
      }

      // Utility methods
      showSuccess(message) {
        this.showToast(message, 'success');
      }

      showError(message) {
        this.showToast(message, 'error');
      }
      
      showWarning(message) {
        this.showToast(message, 'warning');
      }

      showToast(message, type = 'info') {
        const toast = document.createElement('div');
        toast.className = `rental-cart-toast ${type}`;
        toast.textContent = message;
        
        Object.assign(toast.style, {
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          borderRadius: '8px',
          color: 'white',
          zIndex: '10000',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transform: 'translateX(100%)',
          transition: 'transform 0.3s ease'
        });

        if (type === 'success') {
          toast.style.background = '#10b981';
        } else if (type === 'error') {
          toast.style.background = '#ef4444';
        } else if (type === 'warning') {
          toast.style.background = '#f59e0b';
        } else {
          toast.style.background = '#3b82f6';
        }

        document.body.appendChild(toast);
        
        setTimeout(() => {
          toast.style.transform = 'translateX(0)';
        }, 100);

        setTimeout(() => {
          toast.style.transform = 'translateX(100%)';
          setTimeout(() => {
            if (toast.parentNode) {
              toast.parentNode.removeChild(toast);
            }
          }, 300);
        }, 3000);
      }

      // Debug methods
      debugCartState() {
        console.log('🔍 === CART DEBUG ===');
        console.log('👤 Customer ID:', this.customerId);
        console.log('📋 Customer Plan:', this.customerPlan, 'Name:', this.customerPlanName);
        console.log('📊 Cart Length:', this.cart.length);
        console.log('📦 Cart Items:', this.cart);
        
        const cartKey = `rental_cart_${this.customerId || 'guest'}`;
        const savedCart = localStorage.getItem(cartKey);
        console.log('🔑 Storage Key:', cartKey);
        console.log('💾 Storage Data:', savedCart);
        console.log('🔍 === END DEBUG ===');
      }
      


      // Force reload cart from storage to fix inconsistencies
      async forceReloadCart() {
        console.log('🔄 Force reloading cart from storage...');
        
        try {
          // Clear current cart
          this.cart = [];
          
          // Reload from storage
          await this.loadCartFromStorage();
          
          // Update display
          this.updateCartDisplay();
          
          console.log('✅ Cart force reloaded. New length:', this.cart.length);
          return true;
        } catch (error) {
          console.error('❌ Error force reloading cart:', error);
          return false;
        }
      }
      
      // Check and fix cart inconsistencies
      async checkAndFixCart() {
        console.log('🔍 Checking cart for inconsistencies...');
        
        const cartKey = `rental_cart_${this.customerId || 'guest'}`;
        const savedCart = localStorage.getItem(cartKey);
        
        if (!savedCart) {
          console.log('📦 No cart data in storage, nothing to fix');
          return;
        }
        
        try {
          const parsedCart = JSON.parse(savedCart);
          const storageLength = parsedCart.length;
          const memoryLength = this.cart.length;
          
          console.log('📊 Cart consistency check:', {
            storageLength: storageLength,
            memoryLength: memoryLength,
            consistent: storageLength === memoryLength
          });
          
          if (storageLength !== memoryLength) {
            console.warn('⚠️ Cart inconsistency detected! Fixing...');
            this.cart = parsedCart;
            this.updateCartDisplay();
            console.log('✅ Cart inconsistency fixed');
          } else {
            console.log('✅ Cart is consistent');
          }
        } catch (error) {
          console.error('❌ Error checking cart consistency:', error);
        }
      }

      // Ensure cart persistence across page navigation
      ensureCartPersistence() {
        // Ensure floating cart stays visible across navigation
        const floatingCart = document.querySelector('.floating-rental-cart');
        if (floatingCart && this.cart.length > 0) {
          floatingCart.style.display = 'block';
          floatingCart.style.position = 'fixed';
          floatingCart.style.zIndex = '9999';
          floatingCart.style.opacity = '1';
        }
      }
      
      // Check if we're on the cart page and refresh if needed
      checkIfOnCartPage() {
        const cartPage = document.querySelector('.rental-cart-page');
        const currentUrl = window.location.pathname;
        
        console.log('🔍 Checking if on cart page:', {
          currentUrl: currentUrl,
          cartPageElement: !!cartPage,
          isCartPage: currentUrl.includes('rental-cart') || currentUrl.includes('cart')
        });
        
        if (cartPage) {
          console.log('📄 Cart page detected, forcing refresh...');
          // Force refresh the cart display
          this.updateCartDisplay();
          return true;
        } else if (currentUrl.includes('rental-cart') || currentUrl.includes('cart')) {
          console.log('⚠️ Cart page detected by URL, but element not found. Waiting for DOM...');
          // Wait a bit for DOM to be ready
          setTimeout(() => {
            this.checkIfOnCartPage();
          }, 500);
          return true;
        }
        
        // Ensure cart persistence even on non-cart pages
        this.ensureCartPersistence();
        return false;
      }
      
             // Helper method to clean book titles
       cleanBookTitle(title) {
         if (!title) return '';
         return title.replace(/\s+/g, ' ').trim();
       }
       
       // Helper method to update checkout button (delegates to renderCartPage logic)
       updateCheckoutButton(checkoutBtn) {
         // Just trigger a full display update — the renderCartPage method
         // already contains the canonical checkout button logic.
         this.updateCartDisplay();
       }
       
       // Debug method to check customer ID detection
       debugCustomerIdDetection() {
        console.log('🔍 === CUSTOMER ID DETECTION DEBUG ===');
        console.log('👤 Current customer ID:', this.customerId);
        console.log('🌐 Current URL:', window.location.href);
        console.log('📄 Current pathname:', window.location.pathname);
        
        // Check container dataset
        const container = document.querySelector('.book-rental-container, .product-rental-container');
        if (container) {
          console.log('📦 Container found:', {
            className: container.className,
            dataset: container.dataset,
            customerId: container.dataset.customerId
          });
        } else {
          console.log('❌ No container found');
        }
        
        // Check meta tags
        const metaCustomerId = document.querySelector('meta[name="customer-id"]');
        if (metaCustomerId) {
          console.log('🏷️ Meta tag found:', {
            name: metaCustomerId.name,
            content: metaCustomerId.content
          });
        } else {
          console.log('❌ No customer-id meta tag found');
        }
        
        // Check localStorage keys
        const allKeys = Object.keys(localStorage);
        const cartKeys = allKeys.filter(key => key.includes('rental_cart'));
        console.log('🔑 Cart-related localStorage keys:', cartKeys);
        
        cartKeys.forEach(key => {
          try {
            const data = localStorage.getItem(key);
            const parsed = JSON.parse(data);
            console.log(`📦 Key "${key}":`, {
              length: parsed.length,
              items: parsed.map(item => ({ variantId: item.variantId, title: item.title }))
            });
          } catch (error) {
            console.log(`❌ Key "${key}" - Parse error:`, error);
          }
        });
        
        console.log('🔍 === END CUSTOMER ID DEBUG ===');
      }
      
      // Public API for external components to safely interact with cart
      async waitForInitialization() {
        if (this.isInitialized) {
          return Promise.resolve();
        }
        
        if (this.initializationPromise) {
          return this.initializationPromise;
        }
        
        // If no initialization in progress, start it
        return this.init();
      }
      
      // Safe method for external components to add items
      async safeAddToCart(bookData) {
        await this.waitForInitialization();
        return this.addToCart(bookData);
      }
      
      // Safe method for external components to remove items
      async safeRemoveFromCart(variantId) {
        await this.waitForInitialization();
        return this.removeFromCart(variantId);
      }
      
      // Get current cart state safely
      async getCartState() {
        await this.waitForInitialization();
        return {
          cart: [...this.cart],
          length: this.cart.length,
          customerId: this.customerId,
          customerPlan: this.customerPlan,
          customerPlanName: this.customerPlanName,
          isInitialized: this.isInitialized
        };
      }
    };
  }

  // Initialize rental cart when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    console.log('🚀 DOM loaded, initializing Rental Cart System...');
    try {
      window.rentalCart = new window.RentalCart();
      console.log('✅ Rental cart instance created successfully');
      // Set up navigation persistence, but only if floating-cart-manager.js is not present
      window._rentalCartListeners = [];
      if (!window.floatingCartManager) {
        const beforeUnloadListener = () => {
          if (window.rentalCart) {
            window.rentalCart.saveCartToStorage();
          }
        };
        window.addEventListener('beforeunload', beforeUnloadListener);
        window._rentalCartListeners.push(['beforeunload', beforeUnloadListener]);
      }
      // Add cleanup method
      window.cleanupRentalCartListeners = function() {
        if (window._rentalCartListeners) {
          window._rentalCartListeners.forEach(([event, handler]) => {
            if (event === 'beforeunload' || event === 'focus') {
              window.removeEventListener(event, handler);
            } else {
              document.removeEventListener(event, handler);
            }
          });
          window._rentalCartListeners = [];
        }
      };
      // Add debug methods to global scope
      window.debugRentalCart = () => {
        if (window.rentalCart) {
          window.rentalCart.debugCartState();
        } else {
          console.error('❌ Rental cart not initialized');
        }
      };
      window.waitForCart = () => {
        if (window.rentalCart) {
          return window.rentalCart.waitForInitialization();
        } else {
          console.error('❌ Rental cart not initialized');
          return Promise.reject(new Error('Rental cart not initialized'));
        }
      };
      window.safeAddToCart = async (bookData) => {
        if (window.rentalCart) {
          return window.rentalCart.safeAddToCart(bookData);
        } else {
          console.error('❌ Rental cart not initialized');
          return Promise.reject(new Error('Rental cart not initialized'));
        }
      };
      window.safeRemoveFromCart = async (variantId) => {
        if (window.rentalCart) {
          return window.rentalCart.safeRemoveFromCart(variantId);
        } else {
          console.error('❌ Rental cart not initialized');
          return Promise.reject(new Error('Rental cart not initialized'));
        }
      };
      window.getCartState = async () => {
        if (window.rentalCart) {
          return window.rentalCart.getCartState();
        } else {
          console.error('❌ Rental cart not initialized');
          return Promise.reject(new Error('Rental cart not initialized'));
        }
      };
      window.ensureCartPersistence = () => {
        if (window.rentalCart) {
          return window.rentalCart.ensureCartPersistence();
        } else {
          console.error('❌ Rental cart not initialized');
        }
      };
      window.forceReloadCart = () => {
        if (window.rentalCart) {
          return window.rentalCart.forceReloadCart();
        } else {
          console.error('❌ Rental cart not initialized');
          return Promise.resolve(false);
        }
      };
      
      window.checkAndFixCart = () => {
        if (window.rentalCart) {
          return window.rentalCart.checkAndFixCart();
        } else {
          console.error('❌ Rental cart not initialized');
          return Promise.resolve(false);
        }
      };
      
      window.debugCustomerIdDetection = () => {
        if (window.rentalCart) {
          window.rentalCart.debugCustomerIdDetection();
        } else {
          console.error('❌ Rental cart not initialized');
        }
      };
      
      window.reinitializeCart = () => {
        if (window.rentalCart) {
          return window.rentalCart.reinitializeForNewCustomer();
        } else {
          console.error('❌ Rental cart not initialized');
          return Promise.resolve(false);
        }
      };
      
             window.testCheckoutValidation = () => {
         if (window.rentalCart) {
           console.log('🧪 Testing checkout validation...');
           const planLimit = window.rentalCart.getPlanLimit();
           const currentRentals = window.rentalCart._currentRentalsCount || 0;
           const cartItems = window.rentalCart.cart.length;
           const totalAfterCheckout = currentRentals + cartItems;
           
           console.log('📊 Checkout validation test:', {
             planLimit,
             currentRentals,
             cartItems,
             totalAfterCheckout,
             canCheckout: totalAfterCheckout <= planLimit,
             availableSlots: Math.max(0, planLimit - currentRentals - cartItems)
           });
           
           if (totalAfterCheckout > planLimit) {
             console.warn('⚠️ Checkout would exceed plan limit!');
           } else {
             console.log('✅ Checkout validation passed');
           }
         } else {
           console.error('❌ Rental cart not initialized');
         }
       };
       
       window.forceShowCheckoutButton = () => {
         if (window.rentalCart) {
           console.log('🔧 Force showing checkout button with aggressive CSS...');
           const checkoutBtn = document.querySelector('.checkout-btn');
           if (checkoutBtn) {
             console.log('🔍 Checkout button found, applying aggressive styles...');
             
             // Apply aggressive CSS to force visibility
             checkoutBtn.style.display = 'block !important';
             checkoutBtn.style.visibility = 'visible !important';
             checkoutBtn.style.opacity = '1 !important';
             checkoutBtn.style.position = 'relative !important';
             checkoutBtn.style.zIndex = '9999 !important';
             checkoutBtn.style.width = '100% !important';
             checkoutBtn.style.height = 'auto !important';
                           checkoutBtn.style.backgroundColor = '#333333 !important';
             checkoutBtn.style.color = 'white !important';
             checkoutBtn.style.padding = '18px 20px !important';
             checkoutBtn.style.fontSize = '1.2rem !important';
             checkoutBtn.style.fontWeight = '700 !important';
             checkoutBtn.style.border = 'none !important';
             checkoutBtn.style.borderRadius = '8px !important';
             checkoutBtn.style.cursor = 'pointer !important';
             checkoutBtn.style.textAlign = 'center !important';
             
             checkoutBtn.disabled = false;
             checkoutBtn.textContent = '🚀 Place Rental Order (FORCE VISIBLE)';
             
             // Also force the parent elements to be visible
             const checkoutSection = checkoutBtn.closest('.checkout-section');
             if (checkoutSection) {
               checkoutSection.style.display = 'block !important';
               checkoutSection.style.visibility = 'visible !important';
               checkoutSection.style.opacity = '1 !important';
             }
             
             const cartSummary = checkoutBtn.closest('.cart-summary');
             if (cartSummary) {
               cartSummary.style.display = 'block !important';
               cartSummary.style.visibility = 'visible !important';
               cartSummary.style.opacity = '1 !important';
             }
             
             console.log('✅ Checkout button aggressively forced to show');
             console.log('🔍 Final button rect:', checkoutBtn.getBoundingClientRect());
           } else {
             console.error('❌ Checkout button not found');
           }
         } else {
           console.error('❌ Rental cart not initialized');
         }
       };
       
       window.debugCheckoutButton = () => {
         console.log('🔍 === COMPREHENSIVE CHECKOUT BUTTON DEBUG ===');
         
         // Check if we're on the cart page
         const cartPage = document.querySelector('.rental-cart-page');
         console.log('📄 Cart page element found:', !!cartPage);
         
         // Check cart summary
         const cartSummary = document.querySelector('.cart-summary');
         console.log('📊 Cart summary element found:', !!cartSummary);
         if (cartSummary) {
           console.log('📊 Cart summary display:', cartSummary.style.display);
           console.log('📊 Cart summary computed display:', window.getComputedStyle(cartSummary).display);
           console.log('📊 Cart summary visibility:', window.getComputedStyle(cartSummary).visibility);
           console.log('📊 Cart summary opacity:', window.getComputedStyle(cartSummary).opacity);
           console.log('📊 Cart summary classes:', cartSummary.className);
           console.log('📊 Cart summary innerHTML length:', cartSummary.innerHTML.length);
         }
         
         // Check checkout section
         const checkoutSection = document.querySelector('.checkout-section');
         console.log('🚀 Checkout section element found:', !!checkoutSection);
         if (checkoutSection) {
           console.log('🚀 Checkout section display:', checkoutSection.style.display);
           console.log('🚀 Checkout section computed display:', window.getComputedStyle(checkoutSection).display);
           console.log('🚀 Checkout section visibility:', window.getComputedStyle(checkoutSection).visibility);
           console.log('🚀 Checkout section opacity:', window.getComputedStyle(checkoutSection).opacity);
           console.log('🚀 Checkout section classes:', checkoutSection.className);
         }
         
         // Check checkout button
         const checkoutBtn = document.querySelector('.checkout-btn');
         console.log('🔘 Checkout button element found:', !!checkoutBtn);
         if (checkoutBtn) {
           const computedStyles = window.getComputedStyle(checkoutBtn);
           console.log('🔘 Checkout button inline display:', checkoutBtn.style.display);
           console.log('🔘 Checkout button computed display:', computedStyles.display);
           console.log('🔘 Checkout button visibility:', computedStyles.visibility);
           console.log('🔘 Checkout button opacity:', computedStyles.opacity);
           console.log('🔘 Checkout button position:', computedStyles.position);
           console.log('🔘 Checkout button zIndex:', computedStyles.zIndex);
           console.log('🔘 Checkout button width:', computedStyles.width);
           console.log('🔘 Checkout button height:', computedStyles.height);
           console.log('🔘 Checkout button disabled:', checkoutBtn.disabled);
           console.log('🔘 Checkout button text:', checkoutBtn.textContent);
           console.log('🔘 Checkout button classes:', checkoutBtn.className);
           console.log('🔘 Checkout button parent:', checkoutBtn.parentElement?.className);
           console.log('🔘 Checkout button offsetParent:', checkoutBtn.offsetParent);
           console.log('🔘 Checkout button getBoundingClientRect:', checkoutBtn.getBoundingClientRect());
         }
         
         // Check all buttons on the page
         const allButtons = document.querySelectorAll('button');
         console.log('🔘 Total buttons on page:', allButtons.length);
         allButtons.forEach((btn, index) => {
           if (btn.className.includes('checkout') || btn.textContent.includes('checkout') || btn.textContent.includes('Checkout') || btn.textContent.includes('Place Rental Order')) {
             console.log(`🔘 Button ${index} (potential checkout):`, {
               className: btn.className,
               text: btn.textContent.substring(0, 50),
               display: btn.style.display,
               computedDisplay: window.getComputedStyle(btn).display,
               visibility: window.getComputedStyle(btn).visibility,
               opacity: window.getComputedStyle(btn).opacity,
               rect: btn.getBoundingClientRect()
             });
           }
         });
         
         // Check if there are any overlapping elements
         if (checkoutBtn) {
           const rect = checkoutBtn.getBoundingClientRect();
           const elementAtPoint = document.elementFromPoint(rect.left + rect.width/2, rect.top + rect.height/2);
           console.log('🔍 Element at checkout button center:', elementAtPoint?.tagName, elementAtPoint?.className);
         }
         
         console.log('🔍 === END COMPREHENSIVE DEBUG ===');
       };
      
             window.testAbsoluteCheckoutButton = () => {
         console.log('🧪 Creating test checkout button with absolute positioning...');
         
         // Remove any existing test button
         const existingTest = document.getElementById('test-checkout-btn');
         if (existingTest) existingTest.remove();
         
         // Create a test checkout button
         const testBtn = document.createElement('button');
         testBtn.id = 'test-checkout-btn';
         testBtn.textContent = '🚀 TEST CHECKOUT BUTTON (ABSOLUTE)';
         testBtn.style.cssText = `
           position: fixed !important;
           top: 100px !important;
           right: 20px !important;
           z-index: 99999 !important;
                       background: #333333 !important;
           color: white !important;
           padding: 15px 25px !important;
           border: none !important;
           border-radius: 8px !important;
           font-size: 16px !important;
           font-weight: 700 !important;
           cursor: pointer !important;
           box-shadow: 0 4px 20px rgba(16, 185, 129, 0.4) !important;
         `;
         
         testBtn.onclick = () => {
           alert('Test checkout button clicked! This proves the button can be made visible.');
         };
         
         document.body.appendChild(testBtn);
         console.log('✅ Test checkout button created and added to page');
         
         setTimeout(() => {
           testBtn.remove();
           console.log('🗑️ Test checkout button removed after 10 seconds');
         }, 10000);
       };
       
       window.createMissingCheckoutButton = () => {
         console.log('🔧 Creating missing checkout button...');
         
         // Find the cart summary
         const cartSummary = document.querySelector('.cart-summary');
         if (!cartSummary) {
           console.error('❌ Cart summary not found, cannot create checkout button');
           return;
         }
         
                   // Check if checkout section already exists
          let checkoutSection = document.querySelector('.checkout-section');
          if (!checkoutSection) {
            console.log('📝 Creating missing checkout section...');
            checkoutSection = document.createElement('div');
            checkoutSection.className = 'checkout-section';
            checkoutSection.style.cssText = `
              margin: 25px 0;
            `;
            
            // Insert before cart actions
            const cartActions = cartSummary.querySelector('.cart-actions');
            if (cartActions) {
              cartSummary.insertBefore(checkoutSection, cartActions);
            } else {
              cartSummary.appendChild(checkoutSection);
            }
          }
         
         // Check if checkout button already exists
         let checkoutBtn = document.querySelector('.checkout-btn');
         if (!checkoutBtn) {
           console.log('📝 Creating missing checkout button...');
           checkoutBtn = document.createElement('button');
           checkoutBtn.className = 'checkout-btn btn btn-primary btn-lg';
                       checkoutBtn.style.cssText = `
              width: 100%;
              margin: 0;
              padding: 15px 20px;
              font-size: 1rem;
              font-weight: 600;
              border: none;
              border-radius: 8px;
              background: #333333;
              color: white;
              cursor: pointer;
              transition: all 0.2s ease;
              text-align: center;
              box-shadow: 0 4px 15px rgba(16, 185, 129, 0.2);
            `;
            checkoutBtn.textContent = 'Place Rental Order';
           
           checkoutSection.appendChild(checkoutBtn);
         }
         
         // Check if disabled message exists
         let disabledMessage = document.querySelector('.checkout-disabled-message');
         if (!disabledMessage) {
           console.log('📝 Creating missing disabled message...');
           disabledMessage = document.createElement('div');
           disabledMessage.className = 'checkout-disabled-message';
           disabledMessage.style.cssText = `
             display: none;
             text-align: center;
             color: #333333;
             font-size: 14px;
             margin-top: 15px;
             padding: 12px;
                         background: #FFFFF0;
            border: 1px solid #333333;
             border-radius: 6px;
             font-weight: 500;
           `;
           disabledMessage.textContent = '⚠️ Checkout disabled - plan limit reached';
           
           checkoutSection.appendChild(disabledMessage);
         }
         
         console.log('✅ Missing checkout button elements created');
         
         // Now update the button with proper logic
         if (window.rentalCart && checkoutBtn) {
           window.rentalCart.updateCheckoutButton(checkoutBtn);
         }
       };
       
       console.log('🔧 Debug methods added to global scope:');
       console.log('  - debugRentalCart() - Show comprehensive cart state');
       console.log('  - waitForCart() - Wait for cart initialization');
       console.log('  - safeAddToCart(bookData) - Safely add item to cart');
       console.log('  - safeRemoveFromCart(variantId) - Safely remove item from cart');
       console.log('  - getCartState() - Get current cart state safely');
       console.log('  - ensureCartPersistence() - Ensure cart stays visible across navigation');
       console.log('  - forceReloadCart() - Force reload cart from storage');
       console.log('  - checkAndFixCart() - Check and fix cart inconsistencies');
       console.log('  - debugCustomerIdDetection() - Debug customer ID detection');
       console.log('  - reinitializeCart() - Reinitialize cart for new customer');
       console.log('  - testCheckoutValidation() - Test checkout validation logic');
       console.log('  - forceShowCheckoutButton() - Force show checkout button for debugging');
       console.log('  - debugCheckoutButton() - Debug checkout button element and display');
       console.log('  - testAbsoluteCheckoutButton() - Create test button with absolute positioning');
       console.log('  - createMissingCheckoutButton() - Create missing checkout button elements');
      
             // Single deferred refresh after page load to catch late-rendered DOM
       window.addEventListener('load', () => {
         if (window.rentalCart) {
           window.rentalCart.ensureCartPersistence();
           // One refresh after a short delay is enough for Shopify's DOM to settle
           setTimeout(() => {
             window.rentalCart.updateCartDisplay();
           }, 300);
         }
       });
      
    } catch (error) {
      console.error('❌ Error creating rental cart instance:', error);
    }
  });
})();
